import { useId } from 'react';
import Button from '@/components/ui/Button';
import EmptyState from '@/components/ui/EmptyState';
import type { DashboardSectionProps } from './types';

export default function DashboardSection({
  title,
  description,
  action,
  loading = false,
  error,
  onRetry,
  unavailable = false,
  unavailableTitle = 'This information is currently unavailable.',
  empty = false,
  emptyTitle = 'Nothing to show',
  children,
}: DashboardSectionProps) {
  const titleId = useId();
  const loadingLabel = `Loading ${title.toLowerCase()}`;

  let body = children;
  if (loading) {
    body = (
      <div role="status" aria-live="polite" aria-label={loadingLabel} className="space-y-3">
        <span className="sr-only">{loadingLabel}</span>
        {[0, 1, 2].map((row) => (
          <div key={row} aria-hidden="true" className="h-14 animate-pulse rounded-lg bg-gray-100 motion-reduce:animate-none dark:bg-gray-800" />
        ))}
      </div>
    );
  } else if (error) {
    body = (
      <div role="alert" className="rounded-xl bg-danger-50 p-4 text-danger-800 dark:bg-danger-900/20 dark:text-danger-200">
        <p className="break-words text-sm">{error}</p>
        {onRetry && (
          <Button
            type="button"
            variant="outline"
            className="mt-3 min-h-11 motion-reduce:transition-none"
            aria-label={`Retry ${title.toLowerCase()}`}
            onClick={onRetry}
          >
            Retry
          </Button>
        )}
      </div>
    );
  } else if (unavailable) {
    body = (
      <div
        role="status"
        aria-label={`${title} unavailable`}
        className="rounded-xl bg-gray-50 p-4 text-gray-700 dark:bg-gray-800 dark:text-gray-200"
      >
        <p className="break-words text-sm">{unavailableTitle}</p>
      </div>
    );
  } else if (empty) {
    body = <EmptyState title={emptyTitle} className="py-10" />;
  }

  return (
    <section aria-labelledby={titleId} className="min-w-0 rounded-xl bg-white p-4 ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 sm:p-5">
      <div className="mb-4 flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={titleId} className="break-words text-lg font-semibold text-club-navy-950 dark:text-white">{title}</h2>
          {description && <p className="mt-1 break-words text-sm text-gray-600 dark:text-gray-400">{description}</p>}
        </div>
        {action && <div className="min-w-0">{action}</div>}
      </div>
      {body}
    </section>
  );
}
